import { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';

import StrongModal from '../common/StrongModal';
import StrongInput from '../common/StrongInput';
import StrongTextarea from '../common/StrongTextarea';
import StrongSingleSelect from '../common/StrongSingleSelect';

interface ProgramTitle {
  _id: string;
  title: string;
}

interface EditEndPageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, any>) => void;
  selectedEndPage?: any;
  programTitles: ProgramTitle[];
}

const EditEndPageModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedEndPage,
  programTitles,
}: EditEndPageModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [vimeoId, setVimeoId] = useState('');
  const [programId, setProgramId] = useState('');
  const [nextProgramId, setNextProgramId] = useState('');
  const [enableRestart, setEnableRestart] = useState(true);

  useEffect(() => {
    setTitle(selectedEndPage?.title || '');
    setDescription(selectedEndPage?.description || '');
    setImageUrl(selectedEndPage?.imageUrl || '');
    setVimeoId(selectedEndPage?.vimeoId || '');
    setProgramId(selectedEndPage?.programId?._id || selectedEndPage?.programId || '');
    setNextProgramId(
      selectedEndPage?.nextProgramId?._id || selectedEndPage?.nextProgramId || ''
    );
    setEnableRestart(selectedEndPage ? selectedEndPage.enableRestart !== false : true);
  }, [selectedEndPage, isOpen]);

  const handleSubmit = () => {
    onSubmit({
      title,
      description,
      imageUrl,
      vimeoId,
      programId,
      nextProgramId: nextProgramId || null,
      enableRestart,
    });
  };

  const programOptions = programTitles.map((p) => ({ label: p.title, value: p._id }));
  const selectedProgram = programOptions.find((o) => o.value === programId) ?? undefined;
  const selectedNextProgram = programOptions.find((o) => o.value === nextProgramId) ?? undefined;
  const isEditing = !!selectedEndPage;

  return (
    <StrongModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={isEditing ? 'Edit End Page' : 'Create End Page'}
      isSubmitDisabled={!title || !programId}
      body={
        <Tabs variant='enclosed' colorScheme='primary'>
          <TabList mb={4}>
            <Tab fontWeight='semibold'>Details</Tab>
            <Tab fontWeight='semibold'>Next Program</Tab>
          </TabList>

          <TabPanels>
            {/* ── Details tab ── */}
            <TabPanel px={0} pt={0}>
              <VStack spacing={4} align='stretch'>
                <FormControl isRequired>
                  <FormLabel color='gray.600'>Title</FormLabel>
                  <StrongInput
                    placeholder='Enter title'
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color='gray.600'>Description (optional)</FormLabel>
                  <StrongTextarea
                    placeholder='Enter description'
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color='gray.600'>Image URL (optional)</FormLabel>
                  <StrongInput
                    placeholder='Paste image URL'
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color='gray.600'>Vimeo ID (optional)</FormLabel>
                  <StrongInput
                    placeholder='Paste Vimeo ID'
                    value={vimeoId}
                    onChange={(e) => setVimeoId(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color='gray.600'>Program</FormLabel>
                  <StrongSingleSelect
                    options={programOptions}
                    value={selectedProgram}
                    onSelectChange={setProgramId}
                    isDisabled={isEditing}
                  />
                  {isEditing && (
                    <Text fontSize='xs' color='gray.400' mt={1}>
                      Program cannot be changed after creation.
                    </Text>
                  )}
                </FormControl>

                <FormControl>
                  <Flex alignItems='center' gap={3}>
                    <FormLabel color='gray.600' mb={0}>Enable Restart Option</FormLabel>
                    <Switch
                      colorScheme='primary'
                      isChecked={enableRestart}
                      onChange={(e) => setEnableRestart(e.target.checked)}
                    />
                  </Flex>
                </FormControl>
              </VStack>
            </TabPanel>

            {/* ── Next Program tab ── */}
            <TabPanel px={0} pt={0}>
              <VStack spacing={4} align='stretch'>
                <Box
                  p={3}
                  borderRadius='md'
                  borderWidth='1px'
                  borderColor='blue.100'
                  bg='blue.50'
                  fontSize='sm'
                  color='blue.700'
                >
                  Select a program to recommend to users after they complete this program.
                  Leave empty to show no recommendation.
                </Box>
                <FormControl>
                  <FormLabel color='gray.600'>Recommended Next Program</FormLabel>
                  <StrongSingleSelect
                    options={[{ label: '— None —', value: '' }, ...programOptions]}
                    value={selectedNextProgram ?? { label: '— None —', value: '' }}
                    onSelectChange={(val) => setNextProgramId(val === '' ? '' : val)}
                  />
                </FormControl>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      }
    />
  );
};

export default EditEndPageModal;
